import {
  Box,
  Flex,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import ResizeTextarea from 'react-textarea-autosize';
// import { FaCircleArrowUp } from 'react-icons/fa6';
import { AiScoringConstants } from '../aiScoringConstants';
import { KText } from '@/ui-library/components';
import { CharWordCounter } from '@kula-ai/components';
import { Stars03 } from '@kula-ai/icons-core';

const { ICP_MIN_LENGTH, ICP_MIN_LENGTH_MESSAGE, ICP_MAX_CHARACTER_LIMIT } =
  AiScoringConstants;

export const AiIdealCandidateChatBox = ({
  value,
  onChange,
  onSubmit,
  onNoChange,
  highlightedItems,
  _isCategoriesLoading = false,
}: {
  value: string;
  onSubmit: (text: string) => void;
  onNoChange: (text: string) => void; // Called when content hasn't changed
  highlightedItems: string[];
  onChange: (text: string) => void;
  isCategoriesLoading?: boolean;
}) => {
  const [text, setText] = useState(value);
  const [error, setError] = useState('');
  const [originalValue, setOriginalValue] = useState(value);

  // Update original value when the prop value changes
  useEffect(() => {
    // Seeds chat state from props on mount. Deriving during render changes when
    // consumers observe it; out of scope for a relocation.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOriginalValue((prev) => (prev === value ? prev : value));
    setText((prev) => (prev === value ? prev : value));
  }, [value]);

  const header = () => {
    return (
      <Flex flexDir="column" gap="24px" alignItems="center">
        <Stars03
          size={60}
          gradient={{
            direction: 'vertical',
            colors: [
              { offset: '0%', color: '#797DFF' },
              { offset: '100%', color: '#680AF5' },
            ],
          }}
        />
        <KText variant="heading_1" fontWeight="550">
          Describe your ideal candidate for this role
        </KText>
      </Flex>
    );
  };

  const chatBox = () => {
    return (
      <Flex
        border="1px solid transparent"
        p="10px 8px"
        gap="12px"
        w="530px"
        h="auto"
        mt="24px"
        borderRadius="6px"
        boxShadow="0px 1px 2px 0px #1018280D"
        background="linear-gradient( #fff, #fff) padding-box,linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)"
        flexDir="column"
        position="relative"
      >
        <Flex>
          <ResizeTextarea
            minRows={5}
            maxRows={18}
            placeholder="Add as much detail as possible about your ideal candidate for more accurate scoring"
            style={{
              flex: 1,
              height: '100%' as any,
              outline: 'none',
              resize: 'none',
              background: 'none',
              fontSize: '14px',
              paddingRight: '22px', // Adds padding to align content properly
            }}
            autoFocus={true}
            value={text}
            onChange={(e) => {
              const newValue = e.target.value;

              // Prevent spaces as the first character
              if (newValue.startsWith(' ')) {
                return;
              }

              const trimmedValue =
                newValue.length > ICP_MAX_CHARACTER_LIMIT
                  ? newValue.substring(0, ICP_MAX_CHARACTER_LIMIT)
                  : newValue;

              setText(trimmedValue);
              onChange(trimmedValue);
              if (trimmedValue.length >= 30) {
                setError('');
              }
            }}
            onKeyDown={(e) => {
              // Command+Enter (Mac) or Ctrl+Enter (Windows) to submit
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          {/* todo: hide this for now */}
          {/* <Icon
            alignSelf="flex-end"
            as={FaCircleArrowUp as any}
            cursor="pointer"
            w="22px"
            h="22px"
            color="primary-400"
            position="absolute"
            right="8px"
            bottom="8px"
            onClick={handleSubmit}
          /> */}
        </Flex>
      </Flex>
    );
  };

  const handleSubmit = () => {
    if (text.length < ICP_MIN_LENGTH) {
      setError(ICP_MIN_LENGTH_MESSAGE);
      return;
    }
    setError('');

    // Check if content has actually changed
    const hasChanged = text.trim() !== originalValue.trim();

    if (hasChanged) {
      // Content has changed, make API call
      onSubmit(text);
    } else {
      // Content hasn't changed, just switch back to attributes list
      if (onNoChange) {
        onNoChange(text);
      } else {
        // Fallback: if no onNoChange callback, still call onSubmit
        onSubmit(text);
      }
    }
  };

  const highlighter = () => {
    const { highlightItems, thresholdMap } = AiScoringConstants;
    const { dark, light } = thresholdMap.high;

    return (
      <Flex gap="5px" alignItems="center" mt="10px">
        {[...highlightItems].slice(0, 6).map(({ label, id }) => {
          const isHighlighted = highlightedItems.includes(id);
          return (
            <KText
              key={id}
              p="2px 10px"
              borderRadius="6px"
              fontWeight="550"
              border={isHighlighted ? 'none' : '1px solid'}
              borderColor={'secondary-100'}
              bg={isHighlighted ? light : 'transparent'}
              color={isHighlighted ? dark : 'secondary-500'}
              // todo: enable if we want to show loading state for categories
              // opacity={isCategoriesLoading ? 0.6 : 1}
              // transition="opacity 0.2s ease"
            >
              {label}
            </KText>
          );
        })}
        <Popover placement="top">
          {/* @ts-expect-error Chakra PopoverTrigger requires a single child element type it cannot infer */}
          <PopoverTrigger>
            <Box cursor="pointer">
              <KText color="primary-600" fontWeight="550">
                See all
              </KText>
            </Box>
          </PopoverTrigger>
          <PopoverContent w="481px" mr="130px" top="100px">
            <Flex flexDir="column" w="100%">
              <Flex
                justifyContent="space-between"
                w="100%"
                p="12px"
                borderBottom="1px solid"
                borderColor="secondary-100"
              >
                <KText variant="heading_2">Supported attribute types</KText>
                <Flex alignItems="center" gap="6px" cursor="pointer">
                  <Icon />
                  <KText
                    color="primary-600"
                    fontWeight="550"
                    onClick={() => {
                      window.open('https://support.kula.ai/', '_blank');
                    }}
                  >
                    Help
                  </KText>
                </Flex>
              </Flex>
              <Flex
                flexDir="column"
                gap="6px"
                h="100%"
                overflowY="auto"
                maxH="700px"
              >
                {highlightItems.map((item) => (
                  <Flex
                    key={item.id}
                    flexDir="column"
                    gap="6px"
                    p="9px 12px"
                    borderBottom="1px solid"
                    borderColor="secondary-100"
                  >
                    <KText>{item.label}</KText>
                    <KText>{item.description}</KText>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          </PopoverContent>
        </Popover>
      </Flex>
    );
  };

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      w="550px"
      h="100%"
      overflow="hidden"
    >
      {header()}
      {chatBox()}
      {text.length > ICP_MAX_CHARACTER_LIMIT - 50 && (
        <Flex justifyContent="flex-end" w="100%" pr="12px" mt="10px">
          <CharWordCounter value={text} maxLength={ICP_MAX_CHARACTER_LIMIT} />
        </Flex>
      )}
      {error && (
        <KText
          alignSelf="start"
          color="error-200"
          mt="10px"
          pl="12px"
          fontSize="12px"
        >
          {ICP_MIN_LENGTH_MESSAGE}
        </KText>
      )}
      {highlighter()}
    </Flex>
  );
};
